import Alpine from 'alpinejs';
import flatpickr from 'flatpickr';
import {
  getPartnerIconBackground,
  getPartnerIconUrl,
} from '../utils/partnerIcons';

export default function registerCalendarSettingsChannelsStore() {
  const existing = Alpine.store('calendarSettingsChannelsStore');
  if (existing) {
    return existing;
  }
  const baseStore = Alpine.store('calendarSettingsStore');
  if (!baseStore) return null;

  const customStore = {
    // Channels UI / Channex integration
    channexEnabled: false,
    hasProperty: false,
    isCreatingProperty: false,
    showIframe: false,
    iframeUrl: '',
    iframeError: false,
    statusChecked: false,
    syncProgress: null,
    syncWebSocket: null,
    activeChannelTab: 'partners',
    calendarId: null,
    channelsInitialized: false,
    channelsInitPromise: null,
    loadingIframe: false,
    userPermission: '',

    // Hightide-managed Channex channel availability rules
    channelRulesEnabled: false,
    channelRules: [],
    channelRuleChannels: [],
    channelRuleRoomTypes: [],
    channelRulesLoading: false,
    channelRulePollTimer: null,
    channelRuleSaving: false,
    channelRuleError: '',
    showChannelRuleModal: false,
    editingChannelRuleId: null,
    channelRuleStartPicker: null,
    channelRuleEndPicker: null,
    expandedChannelRuleIds: {},
    channelCutOffDays: 1,
    channelCutOffTime: '23:30',
    channelCutoffSaving: false,
    channelRuleWeekdays: [
      { code: 'mo', label: 'Mon' },
      { code: 'tu', label: 'Tue' },
      { code: 'we', label: 'Wed' },
      { code: 'th', label: 'Thu' },
      { code: 'fr', label: 'Fri' },
      { code: 'sa', label: 'Sat' },
      { code: 'su', label: 'Sun' },
    ],
    channelRuleForm: {
      title: '',
      rule_type: 'close_out',
      value: 0,
      channel_ids: [],
      room_type_ids: [],
      start_date: '',
      end_date: '',
      open_ended: false,
      weekdays: ['mo', 'tu', 'we', 'th', 'fr', 'sa', 'su'],
      repeat_yearly: false,
      release_enabled: false,
      release_days_before_arrival: 14,
      is_enabled: true,
    },

    // Partner management
    partners: [],
    partnersLoading: true,
    partnersSyncing: false,
    showAddPartnerModal: false,
    showEditPartnerModal: false,
    editingPartner: {},
    showLegacyGuestEmailPolicyOption: false,
    partnerSaveError: '',
    showAdvancedPartnerSettings: false,
    partnerCountries: [],
    hasInvoicingIntegration: false,
    autoInvoiceEnabled: false,
    otaOptions: [
      { code: 'BOOKING_COM', name: 'Booking.com', commission: 15 },
      { code: 'AIRBNB', name: 'Airbnb', commission: 3 },
      { code: 'HOSTELWORLD', name: 'Hostelworld', commission: 12 },
      { code: 'EXPEDIA', name: 'Expedia', commission: 18 },
      { code: 'HOTELS_COM', name: 'Hotels.com', commission: 18 },
      { code: 'AGODA', name: 'Agoda', commission: 15 },
      { code: 'VRBO', name: 'Vrbo', commission: 8 },
      { code: 'TRIPADVISOR', name: 'TripAdvisor', commission: 15 },
      { code: 'GOOGLE_HOTEL', name: 'Google Hotel Ads', commission: 10 },
      { code: 'PRICELINE', name: 'Priceline', commission: 15 },
      { code: 'MR_AND_MRS_SMITH', name: 'Mr & Mrs Smith', commission: 15 },
      { code: 'CTRIP', name: 'Ctrip', commission: 15 },
      { code: 'TRAVELOKA', name: 'Traveloka', commission: 15 },
      { code: 'HOTELBEDS', name: 'Hotelbeds', commission: 20 },
      { code: 'OTHER_OTA', name: 'Other OTA', commission: 15 },
    ],
    newPartner: {
      name: '',
      partner_type: 'TRAVEL_AGENCY',
      payment_mode: 'FULL_PAYMENT',
      commission_rate: '',
      booking_source_code: '',
      selected_ota: '',
      invoicing_mode: 'CUSTOMER',
      partner_invoice_amount_type: 'FULL',
      collects_commission: false,
      auto_invoice: true,
      bulk_partner_invoices_enabled: false,
      guest_email_policy: 'ALWAYS',
      notification_email: '',
      guest_email_required: true,
      guest_communication_mode: 'INITIAL_ONLY',
      business_name: '',
      vat_number: '',
      billing_address: '',
      billing_city: '',
      billing_postal_code: '',
      billing_country: '',
      billing_email: '',
    },

    async init() {
      if (this.channelsInitialized) return;
      if (this.channelsInitPromise) return this.channelsInitPromise;

      const container = document.getElementById('calendarSettingsContainer');
      this.calendarId = container ? container.dataset.calendarId : null;
      this.userPermission = container?.dataset?.userPermission || '';
      this.hasInvoicingIntegration =
        container?.dataset?.invoicexpressEnabled === 'true';
      this.autoInvoiceEnabled =
        container?.dataset?.autoInvoiceEnabled === 'true';

      this.channelsInitPromise = (async () => {
        try {
          await Promise.allSettled([this.loadPartners(), this.checkStatus()]);
          this.channelsInitialized = true;
        } finally {
          this.channelsInitPromise = null;
        }
      })();

      return this.channelsInitPromise;
    },

    getCsrfToken() {
      const tokenInput = document.querySelector('[name=csrfmiddlewaretoken]');
      if (tokenInput && tokenInput.value) {
        return tokenInput.value;
      }
      const tokenMatch = document.cookie.match(/(?:^|; )csrftoken=([^;]+)/);
      return tokenMatch ? decodeURIComponent(tokenMatch[1]) : '';
    },

    notifyPartner(message, type = 'info') {
      const toastType = type === 'error' ? 'danger' : type;
      if (window.Alpine && Alpine.store('toastStore')) {
        Alpine.store('toastStore').addToast(message, toastType);
      } else {
        console.log(`[${type}] ${message}`);
        if (type === 'error' || type === 'danger') {
          alert(message);
        }
      }
    },

    async fetchPartnerCountries() {
      if (this.partnerCountries.length > 0) return;
      try {
        const response = await fetch('/customers/countries/');
        if (response.ok) {
          const data = await response.json();
          this.partnerCountries = data.countries || [];
        }
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    },

    getPartnerIconUrl(partner) {
      return getPartnerIconUrl(partner);
    },

    getPartnerIconBackground(partner) {
      return getPartnerIconBackground(partner);
    },

    async loadPartners() {
      try {
        this.partnersLoading = true;
        if (!this.calendarId) {
          console.error('Calendar ID not found');
          this.partners = [];
          return;
        }

        const response = await fetch(
          `/partners/api/partners/?calendar_id=${this.calendarId}`,
        );
        if (response.ok) {
          const partners = await response.json();
          this.partners = partners.map((p) => {
            try {
              let partnerInvoiceAmountType = (
                p.partner_invoice_amount_type || 'FULL'
              ).toString();
              if (
                !['FULL', 'FULL_MINUS_COMMISSION'].includes(
                  partnerInvoiceAmountType,
                )
              ) {
                partnerInvoiceAmountType = 'FULL_MINUS_COMMISSION';
              }
              return {
                ...p,
                _previousPaymentMode: p.payment_mode || 'FULL_PAYMENT',
                partner_invoice_amount_type: partnerInvoiceAmountType,
                collects_commission: p.collects_commission || false,
                bulk_partner_invoices_enabled:
                  p.bulk_partner_invoices_enabled || false,
              };
            } catch (e) {
              console.error('Error processing partner:', p, e);
              return {
                ...p,
                _previousPaymentMode: p.payment_mode || 'FULL_PAYMENT',
                partner_invoice_amount_type: 'FULL',
                collects_commission: false,
                bulk_partner_invoices_enabled: false,
              };
            }
          });
        } else {
          console.error('Failed to load partners:', response.status);
          this.partners = [];
        }
      } catch (error) {
        console.error('Error loading partners:', error);
        this.partners = [];
      } finally {
        this.partnersLoading = false;
      }
    },

    async syncPartnersFromChannex() {
      this.partnersSyncing = true;
      try {
        if (!this.calendarId) {
          this.notifyPartner('Calendar ID not found', 'error');
          return;
        }

        const response = await fetch('/partners/api/partners/sync-channex/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken(),
          },
          body: JSON.stringify({ calendar_id: this.calendarId }),
        });
        const result = await response.json();
        if (response.ok) {
          this.notifyPartner(
            `Synced ${result.synced} channel(s): ${
              result.channels.join(', ') || 'none'
            }`,
            'success',
          );
          await this.loadPartners();
        } else {
          this.notifyPartner(
            result.error || 'Failed to sync channels',
            'error',
          );
        }
      } catch (error) {
        console.error('Error syncing channels:', error);
        this.notifyPartner('Failed to sync channels', 'error');
      } finally {
        this.partnersSyncing = false;
      }
    },

    async checkStatus() {
      try {
        const response = await fetch(
          `/channex/api/status/?calendar_id=${this.calendarId}`,
        );
        if (!response.ok) {
          console.error('Failed to fetch Channex status:', response.status);
          this.statusChecked = true;
          return;
        }
        const data = await response.json();
        this.channexEnabled = data.channex_enabled;
        this.channelRulesEnabled = data.channel_rules_enabled;
        this.hasProperty = data.has_property;
        this.channelCutOffDays = data.channel_cut_off_days;
        this.channelCutOffTime = this.alignChannelCutOffTime(
          data.channel_cut_off_time,
        );
        this.showIframe = this.hasProperty;
        this.statusChecked = true;
        if (this.hasProperty) {
          const tasks = [this.loadIframe()];
          if (this.channelRulesEnabled) {
            tasks.push(this.loadChannelRules(), this.loadChannelRuleOptions());
          }
          await Promise.all(tasks);
        }
      } catch (error) {
        console.error('Error checking Channex status:', error);
        this.statusChecked = true;
      }
    },

    alignChannelCutOffTime(value) {
      const match = String(value || '').match(/^(\d{2}):(\d{2})/);
      if (!match) return '23:30';
      const minute = Number(match[2]) < 30 ? '00' : '30';
      return `${match[1]}:${minute}`;
    },

    canManageChannelRules() {
      return ['Owner', 'Admin'].includes(this.userPermission);
    },

    async saveChannelCutoff() {
      this.channelCutoffSaving = true;
      try {
        const response = await fetch('/channex/api/channel-cutoff/', {
          method: 'PATCH',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken(),
          },
          body: JSON.stringify({
            calendar_id: this.calendarId,
            channel_cut_off_days: Number(this.channelCutOffDays),
            channel_cut_off_time: this.channelCutOffTime,
          }),
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(this.channelRuleApiError(data));
        }
        this.channelCutOffDays = data.channel_cut_off_days;
        this.channelCutOffTime = this.alignChannelCutOffTime(
          data.channel_cut_off_time,
        );
        this.notifyPartner('Channel booking cutoff updated', 'success');
      } catch (error) {
        this.notifyPartner(
          error.message || 'Unable to update channel cutoff',
          'error',
        );
      } finally {
        this.channelCutoffSaving = false;
      }
    },

    emptyChannelRuleForm() {
      return {
        title: '',
        rule_type: 'close_out',
        value: 0,
        channel_ids: [],
        room_type_ids: [],
        start_date: '',
        end_date: '',
        open_ended: false,
        weekdays: ['mo', 'tu', 'we', 'th', 'fr', 'sa', 'su'],
        repeat_yearly: false,
        release_enabled: false,
        release_days_before_arrival: 14,
        is_enabled: true,
      };
    },

    async loadChannelRules() {
      if (!this.calendarId || !this.channelRulesEnabled) return;
      this.channelRulesLoading = true;
      try {
        const response = await fetch(
          `/channex/api/channel-rules/?calendar_id=${this.calendarId}`,
        );
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Unable to load rules');
        this.channelRules = data;
        if (this.channelRulePollTimer) {
          clearTimeout(this.channelRulePollTimer);
          this.channelRulePollTimer = null;
        }
        if (
          this.channelRules.some((rule) =>
            ['pending', 'syncing', 'deleting'].includes(rule.sync_status),
          )
        ) {
          this.channelRulePollTimer = setTimeout(
            () => this.loadChannelRules(),
            5000,
          );
        }
      } catch (error) {
        console.error('Failed to load channel rules:', error);
        this.notifyPartner(error.message, 'error');
      } finally {
        this.channelRulesLoading = false;
      }
    },

    async loadChannelRuleOptions() {
      if (!this.calendarId || !this.channelRulesEnabled) return;
      try {
        const response = await fetch(
          `/channex/api/channel-rules/options/?calendar_id=${this.calendarId}`,
        );
        const data = await response.json();
        if (!response.ok)
          throw new Error(data.error || 'Unable to load options');
        this.channelRuleChannels = data.channels || [];
        this.channelRuleRoomTypes = data.room_types || [];
      } catch (error) {
        console.error('Failed to load channel rule options:', error);
        this.notifyPartner(error.message, 'error');
      }
    },

    openCreateChannelRule() {
      this.editingChannelRuleId = null;
      this.channelRuleForm = this.emptyChannelRuleForm();
      this.channelRuleError = '';
      this.showChannelRuleModal = true;
      this.loadChannelRuleOptions();
      this.syncChannelRuleDatePickers();
    },

    openEditChannelRule(rule) {
      this.editingChannelRuleId = rule.id;
      this.channelRuleForm = {
        title: rule.title,
        rule_type: rule.rule_type,
        value: rule.value ?? 0,
        channel_ids: rule.channel_ids.map(String),
        room_type_ids: rule.room_type_ids.map(Number),
        start_date: rule.start_date,
        end_date: rule.end_date || '',
        open_ended: !rule.end_date,
        weekdays:
          rule.weekdays && rule.weekdays.length
            ? [...rule.weekdays]
            : ['mo', 'tu', 'we', 'th', 'fr', 'sa', 'su'],
        repeat_yearly: rule.repeat_yearly,
        release_enabled: rule.release_days_before_arrival !== null,
        release_days_before_arrival:
          rule.release_days_before_arrival === null
            ? 14
            : rule.release_days_before_arrival,
        is_enabled: rule.is_enabled,
      };
      this.channelRuleError = '';
      this.showChannelRuleModal = true;
      this.syncChannelRuleDatePickers();
    },

    initChannelRuleDatePicker(which, element) {
      if (!element || typeof flatpickr !== 'function') return;
      const key =
        which === 'start' ? 'channelRuleStartPicker' : 'channelRuleEndPicker';
      if (this[key] && this[key].input === element) return;
      if (this[key]) this[key].destroy();

      const instance = flatpickr(element, {
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'M j, Y',
        allowInput: false,
        appendTo: document.body,
        onChange: (_selectedDates, dateStr) => {
          if (which === 'start') {
            this.channelRuleForm.start_date = dateStr || '';
          } else {
            this.channelRuleForm.end_date = dateStr || '';
          }
        },
      });
      this[key] = instance;
      this.syncChannelRuleDatePickers();
    },

    syncChannelRuleDatePickers() {
      Alpine.nextTick(() => {
        this.applyChannelRulePickerDate(
          this.channelRuleStartPicker,
          this.channelRuleForm.start_date,
        );
        this.applyChannelRulePickerDate(
          this.channelRuleEndPicker,
          this.channelRuleForm.end_date,
        );
        this.setChannelRuleEndPickerEnabled();
      });
    },

    applyChannelRulePickerDate(picker, value) {
      if (!picker) return;
      if (value) picker.setDate(value, false, 'Y-m-d');
      else picker.clear(false);
    },

    setChannelRuleEndPickerEnabled() {
      const picker = this.channelRuleEndPicker;
      if (!picker) return;
      const disabled = !!this.channelRuleForm.open_ended;
      picker.input.disabled = disabled;
      if (picker.altInput) picker.altInput.disabled = disabled;
    },

    closeChannelRuleModal() {
      this.showChannelRuleModal = false;
      this.channelRuleError = '';
    },

    toggleChannelRuleSelection(field, value) {
      const normalized =
        field === 'room_type_ids' ? Number(value) : String(value);
      const values = this.channelRuleForm[field];
      const index = values.indexOf(normalized);
      if (index >= 0) values.splice(index, 1);
      else values.push(normalized);
    },

    toggleChannelRuleWeekday(code) {
      this.toggleChannelRuleSelection('weekdays', code);
    },

    channelRuleApiError(data) {
      if (data.error) return data.error;
      const first = Object.entries(data)[0];
      if (!first) return 'Unable to save channel rule';
      const value = Array.isArray(first[1]) ? first[1][0] : first[1];
      return `${first[0].replaceAll('_', ' ')}: ${value}`;
    },

    async saveChannelRule() {
      this.channelRuleSaving = true;
      this.channelRuleError = '';
      try {
        const payload = {
          calendar_id: this.calendarId,
          title: this.channelRuleForm.title,
          rule_type: this.channelRuleForm.rule_type,
          value:
            this.channelRuleForm.rule_type === 'close_out'
              ? null
              : Number(this.channelRuleForm.value),
          channel_ids: this.channelRuleForm.channel_ids,
          room_type_ids: this.channelRuleForm.room_type_ids,
          start_date: this.channelRuleForm.start_date,
          end_date: this.channelRuleForm.open_ended
            ? null
            : this.channelRuleForm.end_date || null,
          weekdays: this.channelRuleForm.weekdays,
          repeat_yearly:
            !this.channelRuleForm.open_ended &&
            this.channelRuleForm.repeat_yearly,
          release_days_before_arrival: this.channelRuleForm.release_enabled
            ? Number(this.channelRuleForm.release_days_before_arrival)
            : null,
          is_enabled: this.channelRuleForm.is_enabled,
        };
        const url = this.editingChannelRuleId
          ? `/channex/api/channel-rules/${this.editingChannelRuleId}/`
          : '/channex/api/channel-rules/';
        const response = await fetch(url, {
          method: this.editingChannelRuleId ? 'PATCH' : 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken(),
          },
          body: JSON.stringify(payload),
        });
        const data = await response.json();
        if (!response.ok) {
          this.channelRuleError = this.channelRuleApiError(data);
          return;
        }
        this.closeChannelRuleModal();
        this.notifyPartner(
          this.editingChannelRuleId
            ? 'Channel rule updated and queued for sync'
            : 'Channel rule created and queued for sync',
          'success',
        );
        await this.loadChannelRules();
      } catch (error) {
        this.channelRuleError = error.message || 'Unable to save channel rule';
      } finally {
        this.channelRuleSaving = false;
      }
    },

    async deleteChannelRule(rule) {
      if (!confirm(`Delete channel rule "${rule.title}"?`)) return;
      const response = await fetch(`/channex/api/channel-rules/${rule.id}/`, {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: { 'X-CSRFToken': this.getCsrfToken() },
      });
      if (response.ok) {
        this.channelRules = this.channelRules.filter(
          (item) => item.id !== rule.id,
        );
        this.notifyPartner('Channel rule queued for removal', 'success');
      } else {
        const data = await response.json();
        this.notifyPartner(
          data.error || 'Unable to delete channel rule',
          'error',
        );
      }
    },

    async retryChannelRule(rule) {
      const response = await fetch(
        `/channex/api/channel-rules/${rule.id}/retry/`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken(),
          },
          body: '{}',
        },
      );
      if (response.ok) {
        this.notifyPartner('Channel rule retry queued', 'success');
        await this.loadChannelRules();
      } else {
        const data = await response.json();
        this.notifyPartner(
          data.error || 'Unable to retry channel rule',
          'error',
        );
      }
    },

    isChannelRuleExpanded(ruleId) {
      return !!this.expandedChannelRuleIds[ruleId];
    },

    toggleChannelRuleExpanded(ruleId) {
      this.expandedChannelRuleIds = {
        ...this.expandedChannelRuleIds,
        [ruleId]: !this.expandedChannelRuleIds[ruleId],
      };
    },

    channelRuleEffectLine(rule) {
      const includeYear = !rule.repeat_yearly;
      const start = this.channelRuleBoundary(rule.start_date, includeYear);
      const units = Number(rule.value) === 1 ? 'unit' : 'units';
      const effect =
        {
          close_out: 'No availability',
          max_availability: `Maximum ${rule.value} ${units}`,
          availability_offset: `Hold back ${rule.value} ${units}`,
        }[rule.rule_type] || 'Limited availability';
      if (!rule.end_date) return `${effect} from ${start}.`;
      return `${effect} between ${start} and ${this.channelRuleBoundary(
        rule.end_date,
        includeYear,
      )}.`;
    },

    channelRuleBoundary(value, includeYear) {
      if (!value) return '';
      const [year, month, day] = String(value)
        .slice(0, 10)
        .split('-')
        .map(Number);
      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      if (!month || !day) return value;
      const label = `${String(day).padStart(2, '0')} ${months[month - 1]}`;
      return includeYear ? `${label} ${year}` : label;
    },

    channelRuleReleaseLine(rule) {
      if (
        rule.release_days_before_arrival === null ||
        rule.release_days_before_arrival === undefined
      )
        return '';
      const days = Number(rule.release_days_before_arrival);
      return `Rule disabled ${days} ${
        days === 1 ? 'day' : 'days'
      } before arrival dates`;
    },

    channelRuleTypeLabel(rule) {
      return {
        close_out: 'Close out',
        max_availability: `Maximum ${rule.value} units`,
        availability_offset: `Hold back ${rule.value} units`,
      }[rule.rule_type];
    },

    channelRuleStatusClass(rule) {
      if (rule.sync_status === 'synced') return 'bg-success';
      if (['pending', 'syncing', 'deleting'].includes(rule.sync_status)) {
        return 'bg-warning text-dark';
      }
      if (['retry', 'failed'].includes(rule.sync_status)) return 'bg-danger';
      return 'bg-secondary';
    },

    channelRuleStatusLabel(rule) {
      return (
        {
          synced: 'Synced',
          pending: 'Pending',
          syncing: 'Syncing',
          deleting: 'Removing',
          retry: 'Needs retry',
          failed: 'Failed',
          disabled: 'Disabled',
        }[rule.sync_status] || rule.sync_status
      );
    },

    connectSyncWebSocket() {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/ws/channex-sync/${this.calendarId}/`;

      this.syncWebSocket = new WebSocket(wsUrl);

      this.syncWebSocket.onopen = () => {
        console.log('Sync WebSocket connected');
      };

      this.syncWebSocket.onmessage = (event) => {
        const data = JSON.parse(event.data);
        console.log('Sync progress update:', data);

        if (data.type === 'sync_progress') {
          this.syncProgress = {
            step: data.step,
            progress: data.progress,
            message: data.message,
            details: data.details || {},
          };

          if (data.step === 'complete') {
            setTimeout(() => {
              this.hasProperty = true;
              this.showIframe = true;
              this.isCreatingProperty = false;
              this.syncProgress = null;
              this.disconnectSyncWebSocket();
              this.loadIframe();
            }, 2000);
          }

          if (data.details && data.details.error) {
            setTimeout(() => {
              this.isCreatingProperty = false;
              this.syncProgress = null;
              this.disconnectSyncWebSocket();
            }, 3000);
          }
        }
      };

      this.syncWebSocket.onerror = (error) => {
        console.error('Sync WebSocket error:', error);
      };

      this.syncWebSocket.onclose = () => {
        console.log('Sync WebSocket disconnected');
      };
    },

    disconnectSyncWebSocket() {
      if (this.syncWebSocket) {
        this.syncWebSocket.close();
        this.syncWebSocket = null;
      }
    },

    async createProperty() {
      if (
        !confirm(
          'Create Channex property and sync all Accommodation Items and Listings?',
        )
      ) {
        return;
      }

      this.isCreatingProperty = true;
      this.syncProgress = {
        step: 'starting',
        progress: 0,
        message: 'Initializing...',
        details: {},
      };

      this.connectSyncWebSocket();

      try {
        const response = await fetch('/channex/api/create-property/', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken(),
          },
          body: JSON.stringify({
            calendar_id: this.calendarId,
          }),
        });

        const data = await response.json();
        if (!response.ok) {
          console.error('Failed to create property:', data);
          this.notifyPartner(
            data.error || 'Failed to create property',
            'error',
          );
          this.isCreatingProperty = false;
          this.syncProgress = null;
          this.disconnectSyncWebSocket();
        }
      } catch (error) {
        console.error('Error creating property:', error);
        this.notifyPartner('Failed to create property', 'error');
        this.isCreatingProperty = false;
        this.syncProgress = null;
        this.disconnectSyncWebSocket();
      }
    },

    async loadIframe() {
      if (this.loadingIframe || this.iframeUrl) return;
      this.loadingIframe = true;
      this.iframeError = false;

      try {
        const response = await fetch('/channex/api/generate-token/', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken(),
          },
          body: JSON.stringify({
            calendar_id: this.calendarId,
          }),
        });

        const data = await response.json();
        if (!response.ok || !data.token || !data.server || !data.property_id) {
          this.iframeError = true;
          this.notifyPartner('Failed to open the channel manager', 'error');
          return;
        }
        const params = new URLSearchParams({
          oauth_session_key: data.token,
          app_mode: 'headless',
          redirect_to: '/channels',
          property_id: data.property_id,
          min_stay_type: 'arrival',
        });
        this.iframeUrl = `${data.server}/auth/exchange?${params.toString()}`;
      } catch (error) {
        console.error('Failed to load channel manager:', error);
        this.iframeError = true;
        this.notifyPartner('Failed to open the channel manager', 'error');
      } finally {
        this.loadingIframe = false;
      }
    },

    selectOta(code) {
      if (!code) {
        this.newPartner.name = '';
        this.newPartner.commission_rate = '';
        this.newPartner.booking_source_code = '';
        return;
      }
      const ota = this.otaOptions.find((o) => o.code === code);
      if (ota) {
        this.newPartner.name = ota.name;
        this.newPartner.commission_rate = ota.commission;
        this.newPartner.booking_source_code = ota.code;
      }
    },

    resetNewPartner() {
      this.newPartner = {
        name: '',
        partner_type: 'TRAVEL_AGENCY',
        payment_mode: 'FULL_PAYMENT',
        commission_rate: '',
        booking_source_code: '',
        selected_ota: '',
        invoicing_mode: 'CUSTOMER',
        partner_invoice_amount_type: 'FULL',
        collects_commission: false,
        auto_invoice: false,
        bulk_partner_invoices_enabled: false,
        guest_email_policy: 'ALWAYS',
        notification_email: '',
        guest_email_required: true,
        guest_communication_mode: 'INITIAL_ONLY',
        business_name: '',
        vat_number: '',
        billing_address: '',
        billing_city: '',
        billing_postal_code: '',
        billing_country: '',
        billing_email: '',
      };
      this.showAdvancedPartnerSettings = false;
    },

    async openEditPartner(partner) {
      try {
        await this.fetchPartnerCountries();
        let partnerInvoiceAmountType = (
          partner.partner_invoice_amount_type || 'FULL'
        ).toString();
        if (
          !['FULL', 'FULL_MINUS_COMMISSION'].includes(partnerInvoiceAmountType)
        ) {
          partnerInvoiceAmountType = 'FULL_MINUS_COMMISSION';
        }
        this.editingPartner = {
          ...partner,
          billing_country: partner.billing_country || '',
          partner_invoice_amount_type: partnerInvoiceAmountType,
          collects_commission: partner.collects_commission || false,
          bulk_partner_invoices_enabled:
            partner.bulk_partner_invoices_enabled || false,
          guest_email_policy: partner.guest_email_policy || 'ALWAYS',
          notification_email: partner.notification_email || '',
          guest_email_required: partner.guest_email_required ?? true,
          guest_communication_mode:
            partner.guest_communication_mode || 'INITIAL_ONLY',
          send_partner_notifications:
            partner.send_partner_notifications || false,
          notify_on_request_created:
            partner.notify_on_request_created !== undefined
              ? partner.notify_on_request_created
              : true,
          notify_on_instant_confirmed:
            partner.notify_on_instant_confirmed !== undefined
              ? partner.notify_on_instant_confirmed
              : true,
          notify_on_request_later_confirmed:
            partner.notify_on_request_later_confirmed !== undefined
              ? partner.notify_on_request_later_confirmed
              : true,
        };
        // Backward compatibility: map deprecated partner-only mode to
        // "no guest emails" + "all guest-targeted emails sent to partner".
        if (
          this.editingPartner.guest_communication_mode === 'PARTNER_ONLY_ALL'
        ) {
          this.editingPartner.guest_email_policy = 'NEVER';
          this.editingPartner.guest_communication_mode =
            'GUEST_AND_PARTNER_ALL';
        }
        this.showLegacyGuestEmailPolicyOption =
          this.editingPartner.guest_email_policy === 'CONFIRMATION_ONLY';
        this.partnerSaveError = '';
        this.showEditPartnerModal = true;
      } catch (error) {
        console.error('Error opening edit partner modal:', error);
        this.notifyPartner('Failed to open partner editor', 'error');
      }
    },

    closeEditPartnerModal() {
      this.showEditPartnerModal = false;
      this.editingPartner = {};
      this.showLegacyGuestEmailPolicyOption = false;
      this.partnerSaveError = '';
    },

    handleEditingGuestEmailPolicyChange() {
      if (!this.showLegacyGuestEmailPolicyOption) return;
      if (
        this.editingPartner?.guest_email_policy === 'NEVER' ||
        this.editingPartner?.guest_email_policy === 'ALWAYS'
      ) {
        this.showLegacyGuestEmailPolicyOption = false;
      }
    },

    promptAndCreateAccessCode() {
      if (!this.editingPartner?.id) return;
      const label = window.prompt('Access code label');
      if (label && label.trim()) {
        this.createAccessCode(label.trim());
      }
    },

    promptAndCreateUrlCode() {
      if (!this.editingPartner?.id) return;
      const label = window.prompt('URL code label');
      if (label && label.trim()) {
        this.createUrlCode(label.trim());
      }
    },

    async copyToClipboard(value, successMessage = 'Copied to clipboard') {
      if (!value) return;
      try {
        await navigator.clipboard.writeText(value);
        this._toast(successMessage, 'success');
      } catch (error) {
        console.error('Failed to copy text:', error);
        this._toast('Unable to copy. Please copy manually.', 'danger');
      }
    },

    validatePartnerInvoicingFields(partner) {
      if (partner.invoicing_mode !== 'PARTNER') return null;
      const businessName = (partner.business_name || '').trim();
      const billingEmail = (partner.billing_email || '').trim();
      if (!businessName) {
        return 'Business name is required when invoicing mode is "Invoice Partner".';
      }
      if (!billingEmail) {
        return 'Billing email is required when invoicing mode is "Invoice Partner".';
      }
      return null;
    },

    validatePartnerCommunicationFields(partner) {
      const mode = String(partner.guest_communication_mode || 'INITIAL_ONLY');
      const requiresPartnerEmail = [
        'GUEST_AND_PARTNER_ALL',
        'PARTNER_ONLY_ALL',
      ].includes(mode);
      const notificationEmail = String(partner.notification_email || '').trim();
      if (requiresPartnerEmail && !notificationEmail) {
        return 'Partner notification email is required for this communication mode.';
      }
      return null;
    },
    normalizePartnerCommunicationMode(partner) {
      const notificationEmail = String(partner.notification_email || '').trim();
      if (notificationEmail) return;
      // When no partner email is set, force the hidden/default "no partner emails" mode.
      partner.guest_communication_mode = 'GUEST_ONLY_ALL';
    },

    normalizePartnerUrlNotifications(partner) {
      const notificationEmail = String(partner.notification_email || '').trim();
      if (notificationEmail) return;
      partner.send_partner_notifications = false;
    },
    normalizePartnerBulkInvoicing(partner) {
      if (
        partner?.invoicing_mode === 'PARTNER' &&
        Boolean(partner.bulk_partner_invoices_enabled)
      ) {
        partner.auto_invoice = false;
      }
    },

    async saveEditingPartner() {
      if (!this.editingPartner || !this.editingPartner.id) return;
      this.partnerSaveError = '';
      const validationError = this.validatePartnerInvoicingFields(
        this.editingPartner,
      );
      if (validationError) {
        this.partnerSaveError = validationError;
        this.notifyPartner(validationError, 'error');
        return;
      }
      this.normalizePartnerCommunicationMode(this.editingPartner);
      this.normalizePartnerUrlNotifications(this.editingPartner);
      this.normalizePartnerBulkInvoicing(this.editingPartner);
      const communicationValidationError =
        this.validatePartnerCommunicationFields(this.editingPartner);
      if (communicationValidationError) {
        this.partnerSaveError = communicationValidationError;
        this.notifyPartner(communicationValidationError, 'error');
        return;
      }
      try {
        const payload = {
          name: this.editingPartner.name,
          commission_rate: this.editingPartner.commission_rate || null,
          payment_mode: this.editingPartner.payment_mode,
          invoicing_mode: this.editingPartner.invoicing_mode,
          partner_invoice_amount_type:
            this.editingPartner.partner_invoice_amount_type,
          collects_commission: this.editingPartner.collects_commission || false,
          auto_invoice: this.editingPartner.auto_invoice,
          bulk_partner_invoices_enabled: Boolean(
            this.editingPartner.bulk_partner_invoices_enabled,
          ),
          guest_email_policy:
            this.editingPartner.guest_email_policy || 'ALWAYS',
          notification_email: this.editingPartner.notification_email || '',
          guest_email_required: Boolean(
            this.editingPartner.guest_email_required,
          ),
          guest_communication_mode:
            this.editingPartner.guest_communication_mode || 'INITIAL_ONLY',
          send_partner_notifications:
            this.editingPartner.send_partner_notifications || false,
          notify_on_request_created:
            this.editingPartner.notify_on_request_created !== false,
          notify_on_instant_confirmed:
            this.editingPartner.notify_on_instant_confirmed !== false,
          notify_on_request_later_confirmed:
            this.editingPartner.notify_on_request_later_confirmed !== false,
          business_name: this.editingPartner.business_name || '',
          vat_number: this.editingPartner.vat_number || '',
          billing_address: this.editingPartner.billing_address || '',
          billing_city: this.editingPartner.billing_city || '',
          billing_postal_code: this.editingPartner.billing_postal_code || '',
          billing_country: this.editingPartner.billing_country || '',
          billing_email: this.editingPartner.billing_email || '',
        };
        const response = await fetch(
          `/partners/api/partners/${this.editingPartner.id}/`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': this.getCsrfToken(),
            },
            body: JSON.stringify(payload),
          },
        );
        if (response.ok) {
          const index = this.partners.findIndex(
            (p) => p.id === this.editingPartner.id,
          );
          if (index !== -1) {
            this.partners[index] = {
              ...this.partners[index],
              ...this.editingPartner,
            };
          }
          this.closeEditPartnerModal();
          this.notifyPartner('Partner updated', 'success');
        } else {
          const errorData = await response.json();
          const message =
            typeof errorData === 'object' &&
            errorData !== null &&
            !errorData.detail
              ? Object.values(errorData)
                  .flat()
                  .find((m) => typeof m === 'string') ||
                'Failed to update partner'
              : errorData.detail || 'Failed to update partner';
          this.partnerSaveError = message;
          this.notifyPartner(message, 'error');
        }
      } catch (error) {
        console.error('Error updating partner:', error);
        const msg = 'Failed to update partner';
        this.partnerSaveError = msg;
        this.notifyPartner(msg, 'error');
      }
    },

    async createPartner() {
      try {
        if (!this.calendarId) {
          this.notifyPartner('Calendar ID not found', 'error');
          return;
        }
        const validationError = this.validatePartnerInvoicingFields(
          this.newPartner,
        );
        if (validationError) {
          this.notifyPartner(validationError, 'error');
          return;
        }
        this.normalizePartnerCommunicationMode(this.newPartner);
        this.normalizePartnerUrlNotifications(this.newPartner);
        this.normalizePartnerBulkInvoicing(this.newPartner);
        const communicationValidationError =
          this.validatePartnerCommunicationFields(this.newPartner);
        if (communicationValidationError) {
          this.notifyPartner(communicationValidationError, 'error');
          return;
        }

        const payload = {
          calendar_id: this.calendarId,
          name: this.newPartner.name,
          partner_type: this.newPartner.partner_type,
          payment_mode: this.newPartner.payment_mode,
          commission_rate: this.newPartner.commission_rate || null,
          invoicing_mode: this.newPartner.invoicing_mode,
          partner_invoice_amount_type:
            this.newPartner.partner_invoice_amount_type,
          collects_commission: this.newPartner.collects_commission || false,
          auto_invoice: this.newPartner.auto_invoice,
          bulk_partner_invoices_enabled: Boolean(
            this.newPartner.bulk_partner_invoices_enabled,
          ),
          guest_email_policy: this.newPartner.guest_email_policy || 'ALWAYS',
          notification_email: this.newPartner.notification_email || '',
          guest_email_required: Boolean(this.newPartner.guest_email_required),
          guest_communication_mode:
            this.newPartner.guest_communication_mode || 'INITIAL_ONLY',
          business_name: this.newPartner.business_name || '',
          vat_number: this.newPartner.vat_number || '',
          billing_address: this.newPartner.billing_address || '',
          billing_city: this.newPartner.billing_city || '',
          billing_postal_code: this.newPartner.billing_postal_code || '',
          billing_country: this.newPartner.billing_country || '',
          billing_email: this.newPartner.billing_email || '',
        };

        if (
          this.newPartner.partner_type === 'OTA_CHANNEL' &&
          this.newPartner.booking_source_code
        ) {
          payload.booking_source_code = this.newPartner.booking_source_code;
        }

        const response = await fetch('/partners/api/partners/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken(),
          },
          body: JSON.stringify(payload),
        });
        if (response.ok) {
          this.notifyPartner('Partner created', 'success');
          this.showAddPartnerModal = false;
          this.resetNewPartner();
          await this.loadPartners();
        } else {
          const error = await response.json();
          this.notifyPartner(
            error.error ||
              error.booking_source_code?.[0] ||
              'Failed to create partner',
            'error',
          );
        }
      } catch (error) {
        console.error('Error creating partner:', error);
        this.notifyPartner('Failed to create partner', 'error');
      }
    },

    async deletePartner(partner) {
      if (!confirm(`Delete partner '${partner.name}'?`)) return;
      try {
        const response = await fetch(`/partners/api/partners/${partner.id}/`, {
          method: 'DELETE',
          headers: { 'X-CSRFToken': this.getCsrfToken() },
        });
        if (response.ok) {
          this.notifyPartner('Partner deleted', 'success');
          await this.loadPartners();
        } else {
          this.notifyPartner('Failed to delete partner', 'error');
        }
      } catch (error) {
        console.error('Error deleting partner:', error);
        this.notifyPartner('Failed to delete partner', 'error');
      }
    },

    async updatePartner(partner) {
      try {
        const response = await fetch(`/partners/api/partners/${partner.id}/`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken(),
          },
          body: JSON.stringify({
            commission_rate: partner.commission_rate || null,
            payment_mode: partner.payment_mode,
          }),
        });
        if (response.ok) {
          this.notifyPartner('Partner updated', 'success');
        } else {
          this.notifyPartner('Failed to update partner', 'error');
        }
      } catch (error) {
        console.error('Error updating partner:', error);
        this.notifyPartner('Failed to update partner', 'error');
      }
    },

    confirmPaymentModeChange(partner, event) {
      const newValue = event.target.value;
      if (newValue !== partner._previousPaymentMode) {
        const modeNames = {
          FULL_PAYMENT: 'Full Payment',
          DEPOSIT: 'Commission as Deposit',
          NO_PAYMENT: 'No Payment',
        };
        if (
          confirm(
            'Change payment mode to ' +
              modeNames[newValue] +
              '?\n\nThis will affect all future bookings from this partner. Existing bookings will not be changed.',
          )
        ) {
          partner._previousPaymentMode = newValue;
          this.updatePartner(partner);
        } else {
          partner.payment_mode = partner._previousPaymentMode;
        }
      }
    },

    async createAccessCode(label) {
      if (!this.editingPartner?.id) return;
      try {
        const response = await fetch(
          `/partners/api/partners/${this.editingPartner.id}/access-codes/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': this.getCsrfToken(),
            },
            body: JSON.stringify({ label }),
          },
        );
        if (response.ok) {
          const data = await response.json();
          if (!this.editingPartner.access_codes) {
            this.editingPartner.access_codes = [];
          }
          this.editingPartner.access_codes.push(data);
          this._toast('Access code created', 'success');
        }
      } catch (error) {
        console.error('Error creating access code:', error);
        this._toast('Failed to create access code', 'danger');
      }
    },

    async toggleAccessCode(codeId) {
      if (!this.editingPartner?.id) return;
      try {
        const response = await fetch(
          `/partners/api/partners/${this.editingPartner.id}/access-codes/${codeId}/toggle/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': this.getCsrfToken(),
            },
          },
        );
        if (response.ok) {
          const data = await response.json();
          const code = this.editingPartner.access_codes.find(
            (c) => c.id === codeId,
          );
          if (code) code.is_active = data.is_active;
          this._toast(
            data.is_active ? 'Code activated' : 'Code deactivated',
            'success',
          );
        }
      } catch (error) {
        console.error('Error toggling access code:', error);
      }
    },

    async regenerateAccessCode(codeId) {
      if (!confirm('Are you sure? The old code will no longer work.')) return;
      if (!this.editingPartner?.id) return;
      try {
        const response = await fetch(
          `/partners/api/partners/${this.editingPartner.id}/access-codes/${codeId}/regenerate/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': this.getCsrfToken(),
            },
          },
        );
        if (response.ok) {
          const data = await response.json();
          const code = this.editingPartner.access_codes.find(
            (c) => c.id === codeId,
          );
          if (code) code.code = data.code;
          this._toast('Code regenerated', 'success');
        }
      } catch (error) {
        console.error('Error regenerating access code:', error);
      }
    },

    async deleteAccessCode(codeId) {
      if (!confirm('Are you sure you want to delete this access code?')) return;
      if (!this.editingPartner?.id) return;
      try {
        const response = await fetch(
          `/partners/api/partners/${this.editingPartner.id}/access-codes/${codeId}/`,
          {
            method: 'DELETE',
            headers: {
              'X-CSRFToken': this.getCsrfToken(),
            },
          },
        );
        if (response.ok) {
          this.editingPartner.access_codes =
            this.editingPartner.access_codes.filter((c) => c.id !== codeId);
          this._toast('Access code deleted', 'success');
        }
      } catch (error) {
        console.error('Error deleting access code:', error);
      }
    },

    async createUrlCode(label) {
      if (!this.editingPartner?.id) return;
      try {
        const response = await fetch(
          `/partners/api/partners/${this.editingPartner.id}/url-codes/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': this.getCsrfToken(),
            },
            body: JSON.stringify({ label }),
          },
        );
        if (response.ok) {
          const data = await response.json();
          if (!this.editingPartner.url_codes) {
            this.editingPartner.url_codes = [];
          }
          this.editingPartner.url_codes.push(data);
          this._toast('URL code created', 'success');
        } else {
          this._toast('Failed to create URL code', 'danger');
        }
      } catch (error) {
        console.error('Error creating URL code:', error);
        this._toast('Failed to create URL code', 'danger');
      }
    },

    async toggleUrlCode(codeId) {
      if (!this.editingPartner?.id) return;
      try {
        const response = await fetch(
          `/partners/api/partners/${this.editingPartner.id}/url-codes/${codeId}/toggle/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': this.getCsrfToken(),
            },
          },
        );
        if (response.ok) {
          const data = await response.json();
          const code = this.editingPartner.url_codes.find(
            (c) => c.id === codeId,
          );
          if (code) code.is_active = data.is_active;
          this._toast(
            data.is_active ? 'URL code activated' : 'URL code deactivated',
            'success',
          );
        }
      } catch (error) {
        console.error('Error toggling URL code:', error);
      }
    },

    async regenerateUrlCode(codeId) {
      if (!confirm('Are you sure? The old URL will no longer work.')) return;
      if (!this.editingPartner?.id) return;
      try {
        const response = await fetch(
          `/partners/api/partners/${this.editingPartner.id}/url-codes/${codeId}/regenerate/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': this.getCsrfToken(),
            },
          },
        );
        if (response.ok) {
          const data = await response.json();
          const code = this.editingPartner.url_codes.find(
            (c) => c.id === codeId,
          );
          if (code) {
            code.code = data.code;
            code.url = data.url;
          }
          this._toast('URL code regenerated', 'success');
        }
      } catch (error) {
        console.error('Error regenerating URL code:', error);
      }
    },

    async deleteUrlCode(codeId) {
      if (!confirm('Are you sure you want to delete this URL code?')) return;
      if (!this.editingPartner?.id) return;
      try {
        const response = await fetch(
          `/partners/api/partners/${this.editingPartner.id}/url-codes/${codeId}/`,
          {
            method: 'DELETE',
            headers: {
              'X-CSRFToken': this.getCsrfToken(),
            },
          },
        );
        if (response.ok) {
          this.editingPartner.url_codes = this.editingPartner.url_codes.filter(
            (c) => c.id !== codeId,
          );
          this._toast('URL code deleted', 'success');
        }
      } catch (error) {
        console.error('Error deleting URL code:', error);
      }
    },
  };

  const store = {};
  Object.defineProperties(store, Object.getOwnPropertyDescriptors(baseStore));
  Object.defineProperties(store, Object.getOwnPropertyDescriptors(customStore));

  return Alpine.store('calendarSettingsChannelsStore', store);
}
